import includes from 'lodash/fp/includes';
import isEmpty from 'lodash/fp/isEmpty';
import { type Dispatch, useEffect } from 'react';
import type { AnyAction } from 'redux';

import { config } from '../../config';
import { useAppDispatch } from '../../configuration/setup/hooks';
import { storage } from '../../configuration/setup/storage';
import { isEuTenant, useTenant } from '../../configuration/tokenHandling/tokenSlice';
import { MAP_PATH } from '../../routes/routes';
import { chapters } from './chapters';
import { serviceInfoDialogOpenedAtChapter } from './serviceInfoSlice';

export type QueryParams = {
    info?: string;
};

const EU_ONLY_WHATS_NEW_VERSION = '3.9.0';

const isNewVersion = (version: string) => {
    if (!version) {
        return true;
    }
    const localVersionNumbers = version.split('.');
    const currentVersionNumbers = config.releaseVersion.split('.');

    return !(
        currentVersionNumbers[0] === localVersionNumbers[0] &&
        currentVersionNumbers[1] === localVersionNumbers[1] &&
        currentVersionNumbers[2] === localVersionNumbers[2]
    );
};

const shouldOpenWhatsNewForTenant = (tenant: string) =>
    config.releaseVersion !== EU_ONLY_WHATS_NEW_VERSION || isEuTenant(tenant);

export const triggerServiceInfoDialog = (dispatch: Dispatch<AnyAction>, hash: string, tenant: string) => {
    const isWelcomeDisabled = includes('info=false', hash);
    const isMapUrl = includes(MAP_PATH, hash);

    // Check the location path for map view and a dedicated info flag to disable the service info dialog
    if (!isMapUrl || isWelcomeDisabled) {
        return;
    }

    const releaseVersion = storage.load('releaseVersion');

    if (isEmpty(storage.load('welcome')) && isEmpty(releaseVersion)) {
        dispatch(serviceInfoDialogOpenedAtChapter(chapters.welcome));
    }

    if (
        releaseVersion &&
        config.releaseVersion !== 'dev' &&
        isNewVersion(releaseVersion) &&
        shouldOpenWhatsNewForTenant(tenant)
    ) {
        dispatch(serviceInfoDialogOpenedAtChapter(chapters.whatsNew));
    }

    storage.save('releaseVersion', config.releaseVersion);
};

export const useServiceInfoDialogOnStart = (_showMap: boolean) => {
    const dispatch = useAppDispatch();
    const tenant = useTenant();

    useEffect(() => {
        triggerServiceInfoDialog(dispatch, window.location.hash, tenant);
    }, [dispatch, tenant]);
};
